import { useAuthStore } from "@/stores/authStore";
import * as performanceUtils from "@/utils/performance";
import { fetchCsrfToken } from "./csrf";

const API_BASE_URL = import.meta.env.VITE_API;

// Same host/app, but the legacy (v1) routes are mounted at `/api/...` rather
// than `/api/v2/...`. Dropping the trailing `v2/` yields their base so callers
// can reach endpoints that were never ported to the v2 blueprint set.
export const API_V1_BASE = (API_BASE_URL ?? "").replace(/v2\/?$/, "");

const SUCCESS_MAP = {
  200: { message: "Success", description: "The request completed successfully." },
  201: { message: "Created", description: "The resource was created successfully." },
  202: { message: "Accepted", description: "The request has been accepted for processing." },
  204: { message: "No Content", description: "The request was successful, no content returned." },
};

const ERROR_MAP = {
  400: {
    message: "Bad Request",
    description:
      "The request could not be processed. Check your input and try again.",
  },
  401: { message: "Unauthorized", description: "You must log in first." },
  403: {
    message: "Forbidden",
    description:
      "Access denied. You do not have sufficient permissions to perform this action.",
  },
  404: { message: "Not Found", description: "The resource doesn’t exist." },
  409: { message: "Conflict", description: "The request conflicts with existing data." },
  422: { message: "Validation Error", description: "Some fields are invalid." },
  500: { message: "Server Error", description: "Something went wrong on our side." },
  502: {
    message: "Bad Gateway",
    description:
      "A connected service returned an error. Please try again later.",
  },
  503: {
    message: "Service Unavailable",
    description: "The service is temporarily unavailable. Please try again later.",
  },
};

function nonemptyString(value) {
  if (typeof value !== "string") return "";
  const t = value.trim();
  return t ? t : "";
}

function resolvedErrorTexts(status, json, fallback) {
  const apiMessage = nonemptyString(json.message);
  const apiDescription = nonemptyString(json.description);
  return {
    message: apiMessage || fallback.message || "Request failed",
    description:
      apiDescription ||
      fallback.description ||
      (typeof status !== "undefined" ? String(status) : "An error occurred."),
  };
}

const NETWORK_ERROR = {
  message: "Network Error",
  description: "Could not connect to the server. Please check your internet connection.",
};

export async function fetchClient(
  endpoint,
  { method = "GET", body, headers, params, csrf } = {}
) {
  
  let url = /^https?:\/\//i.test(endpoint)
    ? endpoint
    : `${API_BASE_URL}${endpoint}`;

  if (params && Object.keys(params).length > 0) {
    const qs = new URLSearchParams(params).toString();
    url += `?${qs}`;
  }

  const token = useAuthStore.getState().token;
  const isFormData = body instanceof FormData;

  let csrfToken;
  if (csrf) csrfToken = await fetchCsrfToken();

  // Only declare a JSON body when there is one. Body-less requests (GET,
  // DELETE, ...) must not send Content-Type, and FormData lets the browser
  // set its own multipart boundary.
  const hasJsonBody = body !== undefined && body !== null && !isFormData;

  const finalHeaders = {
    ...(hasJsonBody && { "Content-Type": "application/json" }),
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
  };

  if (isFormData) {
    delete finalHeaders["Content-Type"];
  }

  const startTime = performance.now();
  
  try {
    const response = await fetch(url, {
      method,
      headers: finalHeaders,
      body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    });
    
    const duration = performance.now() - startTime;

    const json = await response.json().catch(() => ({}));

    if (!response.ok) {
      const fallback =
        ERROR_MAP[response.status] || {
          message: json.message || "Request failed",
          description: json.description || response.statusText || "An error occurred.",
        };
      const { message, description } = resolvedErrorTexts(
        response.status,
        json,
        fallback
      );

      const errorObject = {
        status: response.status,
        ok: false,
        message,
        description,
        data: json,
      };
      
      // Log failed API call
      performanceUtils.logApiCall(endpoint, duration, false, {
        status: response.status,
        method,
        error: message,
      });
      
      throw new Error(JSON.stringify(errorObject));
    }

    const mapped = SUCCESS_MAP[response.status] || {
      message: json.message || "Request successful",
      description: json.description || "",
    };

    // Log successful API call
    performanceUtils.logApiCall(endpoint, duration, true, {
      status: response.status,
      method,
      dataSize: JSON.stringify(json).length,
    });

    return {
      status: response.status,
      ok: true,
      message: mapped.message,
      description: mapped.description,
      data: json,
    };

  } catch (err) {
    const duration = performance.now() - startTime;
    
    if (err.message && err.message.startsWith('{')) {
        // This is a structured error from the API (non-2xx response)
        // Already logged in the if (!response.ok) block
        throw err;
    }
    
    // Network error or other exception
    performanceUtils.logApiCall(endpoint, duration, false, {
      status: 0,
      method,
      error: err.message || 'Network error',
      isNetworkError: true,
    });
    
    const networkErrorObject = {
      status: 0,
      ok: false,
      message: NETWORK_ERROR.message,
      description: err.message || NETWORK_ERROR.description,
      data: null,
    };

    throw new Error(JSON.stringify(networkErrorObject));
  }
}